import { useCallback, useMemo } from "react";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import { View, Autocomplete, DateRangePicker } from "@visioni/a3s-ui/components";
import type { DateRange } from "@visioni/a3s-ui/components";
import type { CompilationRowStatus } from "../CompilationRow";

// ---------------------------------------------------------------------------
// Status options
// ---------------------------------------------------------------------------

interface StatusOption {
  readonly id: CompilationRowStatus;
  readonly label: string;
}

const STATUS_OPTIONS: readonly StatusOption[] = [
  { id: "ready", label: "Ready" },
  { id: "queued", label: "Queued" },
  { id: "processing", label: "Processing" },
  { id: "uploading", label: "Uploading" },
  { id: "failed", label: "Failed" },
  { id: "expired", label: "Expired" },
];

const getStatusOptionId = (o: StatusOption): string => o.id;
const getStatusOptionLabel = (o: StatusOption): string => o.label;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CompilationFilters {
  readonly dateRange: DateRange;
  readonly status: CompilationRowStatus | null;
  readonly siteId: string | null;
  readonly cameraId: string | null;
}

export interface CompilationFilterRowProps<TSite extends object, TCamera extends object> {
  readonly filters: CompilationFilters;
  readonly onFiltersChange: (filters: CompilationFilters) => void;
  readonly sites: readonly TSite[];
  readonly cameras: readonly TCamera[];
  readonly getSiteLabel: (site: TSite) => string;
  readonly getSiteId: (site: TSite) => string;
  readonly getCameraLabel: (camera: TCamera) => string;
  readonly getCameraId: (camera: TCamera) => string;
}

export function CompilationFilterRow<TSite extends object, TCamera extends object>({
  filters,
  onFiltersChange,
  sites,
  cameras,
  getSiteLabel,
  getSiteId,
  getCameraLabel,
  getCameraId,
}: CompilationFilterRowProps<TSite, TCamera>) {
  const handleDateRangeChange = useCallback(
    (dateRange: DateRange) => {
      onFiltersChange({ ...filters, dateRange });
    },
    [filters, onFiltersChange],
  );

  const handleSiteChange = useCallback(
    (value: TSite | null) => {
      onFiltersChange({ ...filters, siteId: value ? getSiteId(value) : null });
    },
    [filters, onFiltersChange, getSiteId],
  );

  const handleCameraChange = useCallback(
    (value: TCamera | null) => {
      onFiltersChange({ ...filters, cameraId: value ? getCameraId(value) : null });
    },
    [filters, onFiltersChange, getCameraId],
  );

  const handleStatusChange = useCallback(
    (value: StatusOption | null) => {
      onFiltersChange({ ...filters, status: value?.id ?? null });
    },
    [filters, onFiltersChange],
  );

  const selectedSite = useMemo(
    () => sites.find((s) => getSiteId(s) === filters.siteId) ?? null,
    [sites, filters.siteId, getSiteId],
  );

  const selectedCamera = useMemo(
    () => cameras.find((c) => getCameraId(c) === filters.cameraId) ?? null,
    [cameras, filters.cameraId, getCameraId],
  );

  const selectedStatus = useMemo(() => STATUS_OPTIONS.find((o) => o.id === filters.status) ?? null, [filters.status]);

  return (
    <View style={styles.container}>
      <View style={[styles.field, { zIndex: 4 }]}>
        <Autocomplete<TCamera>
          options={cameras as TCamera[]}
          value={selectedCamera}
          onChange={handleCameraChange}
          getOptionLabel={getCameraLabel}
          getOptionId={getCameraId}
          placeholder="All cameras"
          label="Camera"
        />
      </View>
      <View style={[styles.field, { zIndex: 3 }]}>
        <Autocomplete<TSite>
          options={sites as TSite[]}
          value={selectedSite}
          onChange={handleSiteChange}
          getOptionLabel={getSiteLabel}
          getOptionId={getSiteId}
          placeholder="All sites"
          label="Site"
        />
      </View>
      <View style={[styles.field, { zIndex: 2 }]}>
        <Autocomplete<StatusOption>
          options={STATUS_OPTIONS as StatusOption[]}
          value={selectedStatus}
          onChange={handleStatusChange}
          getOptionLabel={getStatusOptionLabel}
          getOptionId={getStatusOptionId}
          placeholder="All statuses"
          label="Status"
        />
      </View>
      <View style={[styles.field, { zIndex: 1 }]}>
        <DateRangePicker
          value={filters.dateRange}
          onChange={handleDateRangeChange}
          label="Date range"
          placeholder="All dates"
          clearable
        />
      </View>
    </View>
  );
}

CompilationFilterRow.displayName = "CompilationFilterRow";

const styles = StyleSheet.create((theme) => ({
  container: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing.lg,
  },
  field: {
    flexBasis: "22%",
    flexGrow: 1,
    minWidth: 160,
  },
}));
